import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../api/client';
import { extractBetHistoryRows, dedupeBetHistory } from '../../utils/rouletteBetHistory';
import { performGlobalLogout, logout, sessionExpired, login } from './authSlice';

// ─── Helper: user-scoped sessionStorage keys ────────────────────────────────
const getUserKeys = (userId) => ({
    username: `profile_username_${userId}`,
    balance: `profile_balance_${userId}`,
    exp: `profile_exp_${userId}`,
});

const clearUserSessionStorage = (userId) => {
    if (!userId) return;
    const keys = getUserKeys(userId);
    sessionStorage.removeItem(keys.username);
    sessionStorage.removeItem(keys.balance);
    sessionStorage.removeItem(keys.exp);
};

const loadUserFromSessionStorage = (userId) => {
    if (!userId) return { username: '', balance: 0, exp: 0 };
    const keys = getUserKeys(userId);
    return {
        username: sessionStorage.getItem(keys.username) || '',
        balance: parseFloat(sessionStorage.getItem(keys.balance)) || 0,
        exp: parseFloat(sessionStorage.getItem(keys.exp)) || 0,
    };
};

const saveUserToSessionStorage = (userId, data) => {
    if (!userId) return;
    const keys = getUserKeys(userId);
    if (data.username) sessionStorage.setItem(keys.username, data.username);
    if (data.balance !== undefined) sessionStorage.setItem(keys.balance, String(data.balance));
    if (data.exp !== undefined) sessionStorage.setItem(keys.exp, String(data.exp));
};

export const fetchProfile = createAsyncThunk(
    'profile/fetchProfile',
    async (userId, { rejectWithValue }) => {
        if (!userId) return rejectWithValue('No userId provided');
        try {
            const response = await api.get(`/auth/profile/${userId}`);
            return response.data;
        } catch (error) {
            console.error('Profile fetch error:', error);
            return rejectWithValue(error.response?.data || 'Failed to fetch profile');
        }
    }
);



export const fetchmarket = createAsyncThunk(
    'profile/fetchmarket',
    async (userId, { rejectWithValue }) => {
        if (!userId) return rejectWithValue('No userId provided');
        try {
            const response = await api.get(`/bets/exposure/summary/${userId}`);
            return response.data;
        } catch (error) {
            console.error('Profile fetch error:', error);
            return rejectWithValue(error.response?.data || 'Failed to fetch profile');
        }
    }
);


export const fetchAccountStament = createAsyncThunk(
    'profile/fetchAccountStament',
    async (userId, { rejectWithValue }) => {
        if (!userId) return rejectWithValue('No userId provided');

        try {
            const response = await api.get(`/account-statement/${userId}`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 401) throw error;
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch account statement');
        }
    }
);

export const fetchcricketAccountStament = createAsyncThunk(
    'profile/fetchcricketAccountStament',
    async (userId, { rejectWithValue }) => {
        if (!userId) return rejectWithValue('No userId provided');

        try {
            const response = await api.get(`/account-statement/cricket/${userId}`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 401) throw error;
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch account statement');
        }
    }
);



export const fetchCasinoAccountStament = createAsyncThunk(
    'profile/fetchCasinoAccountStament',
    async (userId, { rejectWithValue }) => {
        if (!userId) return rejectWithValue('No userId provided');

        try {
            const response = await api.get(`account-statement/casino/${userId}`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 401) throw error;
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch account statement');
        }
    }
);

// ─── Games Statement ───────────────────────────────────────────────────
export const fetchGamesStatement = createAsyncThunk(
    'profile/fetchGamesStatement',
    async ({ userId, matchId, marketType, selection }, { rejectWithValue }) => {
        try {
            const response = await api.get(
                `/account-statement/description/${userId}/${matchId}/${marketType}?selection=${encodeURIComponent(selection)}`
            );
            return response.data;
        } catch (error) {
            if (error.response?.status === 401) throw error;
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch games statement');
        }
    }
);



// ─── casino  Games Statement ───────────────────────────────────────────────────
export const getCasinogameStatment = createAsyncThunk(
    'profile/getCasinogameStatment',
    async ({ userId, gameId, roundId }, { rejectWithValue }) => {
        try {
            const response = await api.get(
                `/casino/details/${userId}/${gameId}/${roundId}`
            );

            return response.data;
        } catch (error) {
            // Re-throw unauthorized errors
            if (error.response?.status === 401) {
                throw error;
            }

            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                'Failed to fetch casino game statement'
            );
        }
    }
);

// ─── casino  Games Statement bets  histroy ───────────────────────────────────────────────────
export const getCasinoSettlebet = createAsyncThunk(
    'profile/getCasinoSettlebet',
    async (userId  , { rejectWithValue }) => {
        try {
            const response = await api.get(
                `/bets/casino/settle-bets/${userId}`
            );

            return response.data;
        } catch (error) {
            // Re-throw unauthorized errors
            if (error.response?.status === 401) {
                throw error;
            }

            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                'Failed to fetch casino game statement'
            );
        }
    }
);

// ─── casino  Games Statement bets  histroy ───────────────────────────────────────────────────
export const getCasinoOpenbet = createAsyncThunk(
    'profile/getCasinoOpenbet',
    async (userId  , { rejectWithValue }) => {
        try {
            const response = await api.get(
                `/bets/casino/open-bets/${userId}`
            );

            return response.data;
        } catch (error) {
            // Re-throw unauthorized errors
            if (error.response?.status === 401) {
                throw error;
            }

            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                'Failed to fetch casino game statement'
            );
        }
    }
);





const getEmptyProfileState = () => ({
    data: { username: '', balance: 0, exp: 0 },
    fetchmarketData: [],
    AccountStamentData: [],
    GamesStamentData: [],
    CasinogameStatment: [],
    cricketAccountStament: [],
    CasinoOpenbet: [],
    CasinoAccountStament: [],
    CasinoSettlebet: [],
    whatsappData: null,
    profileUserId: null,
    profileRequestId: null,
    loading: false,
    error: null
});

const resetProfileState = (state, userId = state.profileUserId) => {
    Object.assign(state, getEmptyProfileState());
    if (userId) {
        clearUserSessionStorage(userId);
    }
};

const profileSlice = createSlice({
    name: 'profile',
    initialState: getEmptyProfileState(),

    reducers: {

        resetProfile: (state, action) => {
            resetProfileState(state, action.payload);
        },
    },

    extraReducers: (builder) => {
        builder
            // Fetch Profile
            .addCase(fetchProfile.pending, (state, action) => {
                state.profileUserId = action.meta.arg || null;
                state.profileRequestId = action.meta.requestId;
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProfile.fulfilled, (state, action) => {
                if (state.profileRequestId !== action.meta.requestId) {
                    return;
                }

                state.loading = false;
                state.profileRequestId = null;
                state.data = action.payload;

                const userId = action.meta.arg;
                if (userId) {
                    saveUserToSessionStorage(userId, action.payload);
                }
            })
            .addCase(fetchProfile.rejected, (state, action) => {
                if (state.profileRequestId !== action.meta.requestId) {
                    return;
                }

                state.loading = false;
                state.profileRequestId = null;
                state.error = action.payload || 'Failed to fetch profile';
            })

            // Fetch Market
            .addCase(fetchmarket.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchmarket.fulfilled, (state, action) => {
                state.loading = false;
                state.fetchmarketData = action.payload || [];
            })
            .addCase(fetchmarket.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch market';
            })

            // Account Statement
            .addCase(fetchAccountStament.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAccountStament.fulfilled, (state, action) => {
                state.loading = false;
                state.AccountStamentData = action.payload || [];
            })
            .addCase(fetchAccountStament.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch account statement';
            })

            // Games Statement
            .addCase(fetchGamesStatement.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGamesStatement.fulfilled, (state, action) => {
                state.loading = false;
                state.GamesStamentData = action.payload || [];
            })
            .addCase(fetchGamesStatement.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch games statement';
            })

           


            // casino fectyh for accoutn stataemtn gam ssatemnt 
            .addCase(getCasinogameStatment.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCasinogameStatment.fulfilled, (state, action) => {
                state.loading = false;
                state.CasinogameStatment = action.payload;
            })
            .addCase(getCasinogameStatment.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch whatsapp number';
            })


            // casino  sttel bets 
            .addCase(getCasinoSettlebet.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCasinoSettlebet.fulfilled, (state, action) => {
                state.loading = false;
                state.CasinoSettlebet = dedupeBetHistory(extractBetHistoryRows(action.payload));
            })
            .addCase(getCasinoSettlebet.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch whatsapp number';
            })

             // casino open bets 
            .addCase(getCasinoOpenbet.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCasinoOpenbet.fulfilled, (state, action) => {
                state.loading = false;
                state.CasinoOpenbet = dedupeBetHistory(extractBetHistoryRows(action.payload));
            })
            .addCase(getCasinoOpenbet.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch whatsapp number';
            })


              // fetchcricketAccountStament
            .addCase(fetchcricketAccountStament.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchcricketAccountStament.fulfilled, (state, action) => {
                state.loading = false;
                state.cricketAccountStament = action.payload;
            })
            .addCase(fetchcricketAccountStament.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch whatsapp number';
            })


             // fetchCasinoAccountStament
            .addCase(fetchCasinoAccountStament.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCasinoAccountStament.fulfilled, (state, action) => {
                state.loading = false;
                state.CasinoAccountStament = action.payload;
            })
            .addCase(fetchCasinoAccountStament.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch whatsapp number';
            })

            .addCase(login.pending, (state) => {
                resetProfileState(state);
            })
            .addCase(login.fulfilled, (state) => {
                resetProfileState(state);
            })

            // GLOBAL LOGOUT - Clear all user data
            .addCase(logout, (state) => {
                resetProfileState(state);
            })
            .addCase(sessionExpired, (state) => {
                resetProfileState(state);
            })
            .addCase(performGlobalLogout.fulfilled, (state, action) => {
                resetProfileState(state, action.payload?.previousUserId);
            });

              
    },
});

export const { resetProfile } = profileSlice.actions;
export default profileSlice.reducer;